// Подписанная величина: подпись, число, пояснение под ним. Одна на весь продукт.
//
// Заведена 25.08.2026. До неё каждый экран лепил свою: в сводке — рамка и `text-label-lg`, в
// карточке подотчётного лица — без рамки и `text-title-h4` (это 32 точки, вдвое крупнее), в
// пульте — плитка высотой 160 точек со ссылкой, прижатой к нижнему краю. Рядом эти три вида
// читаются как три разные программы, а разный кегль у равнозначных чисел ещё и врёт про
// важность: «оборот выдач за всё время» выглядел вдвое важнее остатка, который и есть ответ
// на главный вопрос.
//
// Число моноширинное всегда. В столбце сумм пропорциональные цифры дают рваный правый край,
// и глаз перестаёт сравнивать величины между строками.

import type { ReactNode } from 'react';

import { cn } from '@/utils/cn';

import { useСвежесть } from './свежесть';

export type MetricTone = 'главный' | 'обычный' | 'важный' | 'тревожный' | 'приглушённый';

const ТОН: Record<MetricTone, string> = {
  главный: 'text-text-strong-950',
  обычный: 'text-text-strong-950',
  важный: 'text-text-strong-950',
  тревожный: 'text-error-base',
  // Приглушён нуль, а не прочитан он хуже: бледный токен давал на белом 2,6:1 и в тёмной теме 4,2:1,
  // то есть «0» превращался в пятно (замер независимой проверки 01.10.2026). Серый «второго
  // плана» отличается от чёрного значения достаточно, и читается при этом на 7:1.
  приглушённый: 'text-text-sub-600',
};

export const КЕГЛЬ_ВЕЛИЧИНЫ: Record<MetricTone, string> = {
  // Ступеней ровно три, и у каждой смысл, а не размер.
  //
  // «Главный» — единственный ответ ЭКРАНА: остаток, который висит на человеке. Ровно один на
  // экран. До 25.08.2026 карточка подотчётного лица набирала его руками на 40 точек мимо
  // этого перечня, а соседние равнозначные числа шли на 18 — разный кегль у равнозначных
  // чисел врёт про важность.
  главный: 'text-title-h4',
  обычный: 'text-label-lg',
  // «Важный» — ответ БЛОКА: итог плитки, итог полосы.
  важный: 'text-title-h5',
  тревожный: 'text-label-lg',
  приглушённый: 'text-label-lg',
};

export interface MetricProps {
  label: string;
  /** Само число уже отформатированным текстом: форматирование денег живёт в одном месте. */
  value: ReactNode;
  /** Что это число значит и откуда взялось. Без пояснения число задаёт вопрос, а не отвечает. */
  note?: ReactNode;
  tone?: MetricTone;
  className?: string;
}

export function Metric({ label, value, note, tone = 'обычный', className }: MetricProps) {
  // Подсветка живёт на самой величине, а не на всей плитке: вспыхивать должно то, что
  // изменилось, а подпись и пояснение не менялись. Сравнивается ровно то, что видит человек —
  // готовая строка, а не сырое число (`свежесть.ts`).
  const свежее = useСвежесть(typeof value === 'string' || typeof value === 'number' ? value : null);
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
      {/* Подпись ПЕРЕНОСИТСЯ, а не режется: обрезка съедала смысл — «– Официа…» и
          «Давность неотчитанн…» не читаются вовсе, а величина без внятной подписи не отвечает
          ни на один вопрос. Высота ряда при этом ровная: подписи выровнены по верху. */}
      {/* Подпись набрана тем же серым второго плана, что и пояснение под числом, а не бледным:
          бледный токен давал на белом 2,6:1 и в тёмной теме 4,2:1 при норме 4,5, и «Начислено»,
          «− Аванс» читались хуже, чем число под ними. Различает подпись и пояснение место
          (над числом и под ним), а не светлота. */}
      <span className='text-balance text-paragraph-xs text-text-sub-600'>{label}</span>
      <span
        className={cn(
          // Поле и отрицательный отступ стоят ВСЕГДА, а не только на время вспышки: иначе
          // число дёргалось бы влево-вправо в тот самый миг, когда человек на него смотрит.
          // Движение, которое двигает содержимое, читается как сбой отрисовки, а не как ответ.
          '-mx-1 w-fit rounded px-1 tabular-nums',
          КЕГЛЬ_ВЕЛИЧИНЫ[tone],
          ТОН[tone],
          // `motion-reduce` обязателен: человек, попросивший компьютер не двигаться, просил об
          // этом и здесь. Значение при этом не пропадает — гаснет только вспышка.
          свежее && 'animate-fresh motion-reduce:animate-none',
        )}
      >
        {value}
      </span>
      {note !== undefined && <span className='text-paragraph-xs text-text-sub-600'>{note}</span>}
    </div>
  );
}

/**
 * Ряд величин равными долями, с разделителями между ними.
 *
 * Равные доли, а не `flex-1` по содержимому: иначе «0,00» занимает четверть строки, а
 * «13 311 961,00» — три четверти, и глазу не за что зацепиться при сравнении соседних
 * экранов. Разделитель вместо зазора: он показывает, что величины одного ряда, а не разных.
 */
export function MetricRow({
  children,
  columns = 4,
  className,
}: {
  children: ReactNode;
  columns?: 2 | 3 | 4 | 5 | 6;
  className?: string;
}) {
  // Доли раскладываются по ширине КОНТЕЙНЕРА, а не окна. Разница не теоретическая: полоса
  // итогов ведомости стоит в правой колонке рядом со списком объектов, и на ноутбучном экране
  // ей достаётся 660 точек при ширине окна 1280. Медиазапросы видели «широкое окно», давали
  // пять долей по 130 точек, и подписи резались посреди слова: «– Официа…», «= Всего к вы…».
  // Замер 27.08.2026, снимок ведомости на ноутбуке.
  //
  // `auto-fit` с минимальной шириной доли решает это без плагина контейнерных запросов: сколько
  // долей влезло по 11rem, столько и будет, остальные переносятся строкой ниже. На широком
  // экране раскладка прежняя — доли просто не упираются в минимум.
  const сетка = {
    2: 'bark-metrics-2',
    3: 'bark-metrics-3',
    4: 'bark-metrics-4',
    5: 'bark-metrics-5',
    6: 'bark-metrics-6',
  }[columns];
  return <div className={cn('[&>*]:min-w-0', сетка, className)}>{children}</div>;
}
